import React, { Fragment, useState, useEffect } from 'react';
import type { History } from 'history';
import accounting from 'accounting';
import { createUseStyles } from 'react-jss';
import { DataTableNaked, Flyup } from '@spoiler-alert/ui-library';
import { useQuery } from '@apollo/client';
import { AwardSummaryQuery } from '../../graphql/queries';
import { getColumnsFromDataTableProfile } from '../../components/data-table';
import routePaths from '../../route-paths';
import { weightWithUnit } from '../../utilities/unit-helper';
import { v4 } from 'uuid';
import { AwardSummary, AwardSummaryLine, AwardSummaryTotals, User } from '../../graphql/query-result-types';
import type { DataTableNakedType } from '../../components/component-types';

const useStyles = createUseStyles({
  buyer__flyup__space: {},
});

interface BuyersFlyupProps {
  selectedInventoryId: string;
  user: User;
  history: History;
}

const BuyersFlyup: React.FC<BuyersFlyupProps> = ({ user, selectedInventoryId, history }) => {
  const classes = useStyles();
  const [buyerList, setBuyerList] = useState<AwardSummaryLine[]>([]);
  const [totals, setTotals] = useState<AwardSummaryTotals[]>([]);

  const { data, loading, error } = useQuery<AwardSummary>(AwardSummaryQuery, { variables: { inventoryId: selectedInventoryId } });
  useEffect(() => {
    if (!loading && !error && data) {
      const newBuyerList = data.currentUserQuery.getAwardSummary.results.map((buyer) => ({
        ...buyer,
        _id: `${buyer.transactionLocationName}-${buyer.siteName}-${buyer.logisticsTerm}-${v4()}`,
      }));
      setBuyerList(newBuyerList);
      setTotals(data.currentUserQuery.getAwardSummary.totals);
    }
  }, [data, loading, error]);

  const getColumnOverrides = () => {
    return {
      transactionLocationName: {
        groupHeader: (groupKey: string) => groupKey,
      },
      poundsAwarded: {
        groupSummary: (rows: AwardSummaryLine[]) => {
          const value = rows.reduce((sum, row) => sum + row.poundsAwarded, 0);
          return weightWithUnit(value, user);
        },
      },
      acceptedOffer: {
        groupSummary: (rows: AwardSummaryLine[]) => accounting.formatMoney(rows.reduce((sum, row) => sum + row.acceptedOffer, 0)),
      },
      palletsAwarded: {
        groupSummary: (rows: AwardSummaryLine[]) =>
          accounting.formatNumber(
            rows.reduce((sum, row) => sum + row.palletsAwarded, 0),
            2,
          ),
      },
      costRecoveryRate: {
        groupSummary: (rows: AwardSummaryLine[]) => {
          const totalCost = rows.reduce((sum, row) => sum + row.totalCost, 0);
          const acceptedOffer = rows.reduce((sum, row) => sum + row.acceptedOffer, 0);
          return `${((acceptedOffer / totalCost) * 100).toFixed(2)}%`;
        },
      },
      priceOfInventoryAwarded: {
        groupSummary: (rows: AwardSummaryLine[]) => accounting.formatMoney(rows.reduce((sum, row) => sum + row.priceOfInventoryAwarded, 0)),
      },
      totalPrice: {
        groupSummary: (rows: AwardSummaryLine[]) => accounting.formatMoney(rows.reduce((sum, row) => sum + row.totalPrice, 0)),
      },
      casesAwarded: {
        groupSummary: (rows: AwardSummaryLine[]) =>
          accounting.formatNumber(
            rows.reduce((sum, row) => sum + row.casesAwarded, 0),
            0,
          ),
      },
      discountPercent: {
        groupSummary: (rows: AwardSummaryLine[]) => {
          const totalPrice = rows.reduce((sum, row) => sum + row.totalPrice, 0);
          const acceptedOffer = rows.reduce((sum, row) => sum + row.acceptedOffer, 0);
          const result = acceptedOffer && totalPrice ? (1 - acceptedOffer / totalPrice) * 100 : null;
          return result === null || isNaN(result) ? undefined : `${result.toFixed(2)}%`;
        },
      },
      writeOffDiscountPercent: {
        groupSummary: (rows: AwardSummaryLine[]) => {
          const acceptedOffer = rows.reduce((sum, row) => sum + row.acceptedOffer, 0);
          const writeOffPriceOfInventoryAwarded = rows.reduce((sum, row) => sum + row.writeOffPriceOfInventoryAwarded, 0);
          const result = acceptedOffer && writeOffPriceOfInventoryAwarded ? (1 - acceptedOffer / writeOffPriceOfInventoryAwarded) * 100 : null;
          return result === null || isNaN(result) ? undefined : `${result.toFixed(2)}%`;
        },
      },
      unitPriceRecoveryRate: {
        groupSummary: (rows: AwardSummaryLine[]) => {
          const acceptedOffer = rows.reduce((sum, row) => sum + row.acceptedOffer, 0);
          const unitPriceRecoveryRate = rows.reduce((sum, row) => sum + row.unitPriceRecoveryRate, 0);
          const result = acceptedOffer && unitPriceRecoveryRate ? (acceptedOffer / unitPriceRecoveryRate) * 100 : null;
          return result === null || isNaN(result) ? undefined : `${result.toFixed(2)}%`;
        },
      },
    };
  };

  const getColumns = () => [
    ...getColumnsFromDataTableProfile('Offer Comp Buyer Flyup and Offer Review', user.site.dataTableProfiles, getColumnOverrides()),
  ];

  const handleRowClick = (row: AwardSummaryLine) => {
    history.push(`${routePaths.offerReview}/${row.siteId}/${encodeURIComponent(row.siteName)}`);
  };

  const uniqueBuyersSize = () =>
    buyerList.reduce((set, row) => {
      set.add(row.siteName);
      return set;
    }, new Set()).size;

  const TypedDataTableNaked = DataTableNaked as DataTableNakedType<AwardSummaryLine>;

  return (
    <Fragment>
      <Flyup title={`Buyers (${uniqueBuyersSize()})`} totals={totals} closeOnNavigate={true}>
        <TypedDataTableNaked
          data={buyerList}
          columns={getColumns()}
          onRowClick={(row: AwardSummaryLine) => handleRowClick(row)}
          groupBy={(row: AwardSummaryLine) => row.siteName}
        />
        <div className={classes.buyer__flyup__space} />
      </Flyup>
    </Fragment>
  );
};

export default BuyersFlyup;
